import {Middleware} from 'redux';

import {FrontWindow} from '../../libs/shared/helpers/src/browser/browserHelpers';
import {ExtendedError} from '../../libs/shared/helpers/src/types/customErrors';
import {reportToBugsnag} from '../../srcCommon/services/bugsnag/bugsnag';
import {AppAction} from '../store/appActions';
import {AppState} from '../store/appState';
import {Dispatch} from '../store/appStore';
import {AppPanelActionsEnum} from '../store/panels/app/appPanelActions';
import {pushPathAction, replacePathAction} from '../store/panels/app/appPanelRouteActions';
import {makeGetAppPath} from '../store/panels/app/appPanelSelectors';
import {AppPanelId} from '../store/panels/panelStateBase';

/*
 * API.
 */

/** Navigates the app panel based on the browser's location. */
export function setupAppNavigationFromHistory(window: FrontWindow, panelId: AppPanelId, dispatch: Dispatch) {
  // Navigate to the browser's current location.
  dispatch(replacePathAction(panelId, getWindowPath(window)));

  // Navigate when the browser location changes.
  const onPopState = () => {
    dispatch(pushPathAction(panelId, getWindowPath(window)));
  };

  window.addEventListener('popstate', onPopState);
  return () => window.removeEventListener('popstate', onPopState);
}

/** Updates the browser's location based on the app panel navigation. */
export function createAppNavigationToHistoryMiddleware(
  window: FrontWindow,
  panelId: AppPanelId,
): Middleware<{}, AppState, Dispatch> {
  return ({getState}) => {
    const getAppPath = makeGetAppPath();

    const updateWindowPath = (navigationShouldReplace: boolean) => {
      const state = getState();
      const path = getAppPath(state, {panelId});
      if (path === getWindowPath(window)) {
        return;
      }
      if (navigationShouldReplace) {
        window.history.replaceState(null, '', path);
      } else {
        window.history.pushState(null, '', path);
      }
    };

    return (next) => (action: AppAction) => {
      const result = next(action);
      try {
        if (action.type === AppPanelActionsEnum.NAVIGATED) {
          updateWindowPath(action.payload.navigationShouldReplace);
        }
      } catch (error) {
        reportToBugsnag(new ExtendedError('Failed to update window history', error));
      }

      return result;
    };
  };
}

/*
 * Helpers.
 */

function getWindowPath(window: FrontWindow) {
  const {pathname, search} = window.location;
  return `${pathname}${search}`;
}
